<template>
  <div
    v-if="showTicketsCreadosResueltos || showTiempoPromedio || showTicketsPorTecnico || showTicketsPorArea || showProblemasMasComunes"
    class="stats-dashboard"
  >
    <!-- Vista 1: Total de tickets creados y resueltos -->
    <div v-if="showTicketsCreadosResueltos" class="stats-view">
      <div class="stats-container">
        <div class="stats-header">
          <h2>Panel de Tickets - Creados y Resueltos</h2>
          <button class="close-btn" @click="cerrarTicketsCreadosResueltos">✕</button>
        </div>
        <div class="stats-content">

          <!-- Filtros de fecha específica -->
          <div class="date-filters">
            <div class="date-filter-group">
              <label for="fechaInicio">Fecha Inicio:</label>
              <input 
                type="date" 
                id="fechaInicio" 
                v-model="fechaInicio" 
                @change="actualizarDatosPorFecha"
                class="date-input"
              />
            </div>
            <div class="date-filter-group">
              <label for="fechaFin">Fecha Fin:</label>
              <input 
                type="date" 
                id="fechaFin" 
                v-model="fechaFin" 
                @change="actualizarDatosPorFecha"
                class="date-input"
              />
            </div>
            <div class="date-filter-group">
              <label for="añoSelect">Año:</label>
              <select 
                id="añoSelect" 
                v-model="añoSeleccionado" 
                @change="actualizarDatosPorAño"
                class="year-select"
              >
                <option value="">Seleccionar año</option>
                <option v-for="año in añosDisponibles" :key="año" :value="año">
                  {{ año }}
                </option>
              </select>
            </div>
            <div class="date-filter-group">
              <label for="mesSelect">Mes:</label>
              <select 
                id="mesSelect" 
                v-model="mesSeleccionado" 
                @change="actualizarDatosPorMes"
                class="month-select"
                :disabled="!añoSeleccionado"
              >
                <option value="">Seleccionar mes</option>
                <option v-for="(mes, index) in meses" :key="index" :value="index + 1">
                  {{ mes }}
                </option>
              </select>
            </div>
            <button @click="limpiarFiltros" class="clear-filters-btn">
              Limpiar Filtros
            </button>
          </div>
          
          <!-- Estadísticas principales -->
          <div class="stats-summary">
            <div class="stat-card created">
              <div class="stat-number">{{ ticketsCreados }}</div>
              <div class="stat-label">Tickets Creados</div>
            </div>
            <div class="stat-card resolved">
              <div class="stat-number">{{ ticketsResueltos }}</div>
              <div class="stat-label">Tickets Resueltos</div>
            </div>
            <div class="stat-card pending">
              <div class="stat-number">{{ ticketsPendientes }}</div>
              <div class="stat-label">Tickets Pendientes</div>
            </div>
            <div class="stat-card rate">
              <div class="stat-number">{{ tasaResolucion }}%</div>
              <div class="stat-label">Tasa de Resolución</div>
            </div>
          </div>

          <!-- Gráfico de barras -->
          <div class="chart-container">
            <h4>Evolución por {{ periodoActivoTexto }}</h4>
          <div class="bar-chart">
            <div v-for="item in datosGraficoNormalizados" :key="item.etiqueta" class="bar-item">
              <div class="bar-label">{{ item.etiqueta }}</div>
              <div class="single-bar-container">
                <div
                  class="single-bar"
                  :class="`bar-${item.className}`"
                  :style="{ height: item.altura + '%' }"
                >
                  <span v-if="item.valor > 0" class="bar-value">{{ item.valor }}</span>
                </div>
              </div>
              <div class="bar-metrics">
                <span
                  v-for="(linea, index) in item.detalles"
                  :key="index"
                  class="metric-line"
                >
                  {{ linea }}
                </span>
              </div>
            </div>
          </div>
          </div>
        </div>
        <div class="stats-actions">
          <button @click="cerrarTicketsCreadosResueltos">Cerrar</button>
        </div>
      </div>
    </div>

    <!-- Vista 2: Tiempo promedio de resolución -->
    <div v-if="showTiempoPromedio" class="stats-view">
      <div class="stats-container">
        <div class="stats-header">
          <h2>Panel de Tiempo Promedio de Resolución</h2>
          <button class="close-btn" @click="cerrarTiempoPromedio">✕</button>
        </div>
        <div class="stats-content">
          <div class="time-stats">
            <div class="time-card general">
              <div class="time-icon">📈</div>
              <div class="time-info">
                <div class="time-label">Tiempo General</div>
                <div class="time-value">{{ tiempoPromedioGeneral }}</div>
                <div class="time-tickets">{{ totalTicketsProcesados }} tickets</div>
              </div>
            </div>
            <div class="time-card high">
              <div class="time-icon">⚡</div>
              <div class="time-info">
                <div class="time-label">Prioridad Alta</div>
                <div class="time-value">{{ tiempoAlta }}</div>
                <div class="time-tickets">{{ ticketsAlta }} tickets</div>
              </div>
            </div>
            <div class="time-card medium">
              <div class="time-icon">📋</div>
              <div class="time-info">
                <div class="time-label">Prioridad Media</div>
                <div class="time-value">{{ tiempoMedia }}</div>
                <div class="time-tickets">{{ ticketsMedia }} tickets</div>
              </div>
            </div>
            <div class="time-card low">
              <div class="time-icon">⏳</div>
              <div class="time-info">
                <div class="time-label">Prioridad Baja</div>
                <div class="time-value">{{ tiempoBaja }}</div>
                <div class="time-tickets">{{ ticketsBaja }} tickets</div>
              </div>
            </div>
          </div>
          
          <!-- Gráfico de tiempo por área -->
          <div class="time-chart">
            <h4>Tiempo Promedio por Área</h4>
            <div class="time-bars">
              <div v-for="area in tiempoPorArea" :key="area.nombre" class="time-bar-item">
                <div class="time-bar-label">{{ area.nombre }}</div>
                <div class="time-bar-container">
                  <div class="time-bar-fill" :style="{ width: area.porcentaje + '%' }"></div>
                  <span class="time-bar-value">{{ area.tiempo }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Gráfico de barras por prioridad -->
          <div class="priority-chart">
            <h4>Tiempo Promedio por Prioridad</h4>
            <div class="priority-bars priority-bars-vertical">
              <div
                v-for="item in prioridadChart"
                :key="item.label"
                class="priority-bar-item vertical"
              >
                <div class="priority-label">{{ item.label }}</div>
                <div class="priority-bar-box">
                  <div
                    class="priority-bar-fill"
                    :class="item.className"
                    :style="{ height: item.height + '%' }"
                  ></div>
                </div>
                <div class="priority-bar-value vertical">{{ item.valorTexto }}</div>
                <div class="priority-bar-meta vertical">{{ item.tickets }} tickets</div>
              </div>
            </div>
          </div>
        </div>
        <div class="stats-actions">
          <button @click="cerrarTiempoPromedio">Cerrar</button>
        </div>
      </div>
    </div>

    <!-- Vista 3: Tickets atendidos por técnicos -->
    <div v-if="showTicketsPorTecnico" class="stats-view">
      <div class="stats-container">
        <div class="stats-header">
          <h2>👥 Panel de Tickets por Técnicos</h2>
          <button class="close-btn" @click="cerrarTicketsPorTecnico">✕</button>
        </div>
        <div class="stats-content">
          <div class="stats-summary">
            <div class="stat-card">
              <div class="stat-number">{{ totalTecnicos }}</div>
              <div class="stat-label">Técnicos Activos</div>
            </div>
            <div class="stat-card">
              <div class="stat-number">{{ totalTicketsAtendidos }}</div>
              <div class="stat-label">Total Tickets Atendidos</div>
            </div>
            <div class="stat-card">
              <div class="stat-number">{{ promedioPorTecnico }}</div>
              <div class="stat-label">Promedio por Técnico</div>
            </div>
          </div>
          
          <!-- Gráfico de barras por técnico -->
          <div class="tech-chart">
            <h4>Rendimiento por Técnico</h4>
            <div class="tech-bars">
              <div v-for="tecnico in tecnicosData" :key="tecnico.nombre" class="tech-bar-item">
                <div class="tech-bar-label">{{ tecnico.nombre }}</div>
                <div class="tech-bar-container">
                  <div class="tech-bar-fill" :style="{ width: (tecnico.atendidos / 35 * 100) + '%' }"></div>
                  <span class="tech-bar-value">{{ tecnico.atendidos }} tickets</span>
                </div>
              </div>
            </div>
          </div>

          <div class="tech-table">
            <table>
              <thead>
                <tr>
                  <th>Técnico</th>
                  <th>Tickets Atendidos</th>
                  <th>Tickets Resueltos</th>
                  <th>Tickets Pendientes</th>
                  <th>Tiempo Promedio</th>
                  <th>Eficiencia</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="tecnico in tecnicosData" :key="tecnico.nombre">
                  <td class="tech-name">
                    <div class="tech-avatar">{{ tecnico.iniciales }}</div>
                    {{ tecnico.nombre }}
                  </td>
                  <td>{{ tecnico.atendidos }}</td>
                  <td>{{ tecnico.resueltos }}</td>
                  <td>{{ tecnico.pendientes }}</td>
                  <td>{{ tecnico.tiempoPromedio }}</td>
                  <td>
                    <span :class="['efficiency', getEfficiencyClass(tecnico.eficiencia)]">
                      {{ tecnico.eficiencia }}%
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div class="stats-actions">
          <button @click="cerrarTicketsPorTecnico">Cerrar</button>
        </div>
      </div>
    </div>

    <!-- Vista 4: Tickets por área solicitada -->
    <div v-if="showTicketsPorArea" class="stats-view">
      <div class="stats-container">
        <div class="stats-header">
          <h2>Panel de Tickets por Área</h2>
          <button class="close-btn" @click="cerrarTicketsPorArea">✕</button>
        </div>
        <div class="stats-content">
          <!-- Cards con indicador diario -->
          <div class="area-stats">
            <div class="area-card biomedica">
              <div class="area-icon">🔬</div>
              <div class="area-info">
                <div class="area-name">Biomédica</div>
                <div class="area-tickets">{{ ticketsBiomedica }}</div>
                <div class="area-percentage">{{ porcentajeBiomedica }}%</div>
                <div class="area-daily-indicator">
                  <span class="daily-label">📅 Promedio Diario:</span>
                  <span class="daily-value">{{ ticketsBiomedicaDiario }} tickets/día</span>
                  <span :class="['daily-trend', getTrendClass(ticketsBiomedicaTendencia)]">
                    {{ getTrendArrow(ticketsBiomedicaTendencia) }}
                    {{ Math.abs(ticketsBiomedicaTendencia) }}%
                  </span>
                </div>
              </div>
            </div>
            <div class="area-card tecnologias">
              <div class="area-icon">💻</div>
              <div class="area-info">
                <div class="area-name">Tecnologías</div>
                <div class="area-tickets">{{ ticketsTecnologias }}</div>
                <div class="area-percentage">{{ porcentajeTecnologias }}%</div>
                <div class="area-daily-indicator">
                  <span class="daily-label">📅 Promedio Diario:</span>
                  <span class="daily-value">{{ ticketsTecnologiasDiario }} tickets/día</span>
                  <span :class="['daily-trend', getTrendClass(ticketsTecnologiasTendencia)]">
                    {{ getTrendArrow(ticketsTecnologiasTendencia) }}
                    {{ Math.abs(ticketsTecnologiasTendencia) }}%
                  </span>
                </div>
              </div>
            </div>
            <div class="area-card mantenimiento">
              <div class="area-icon">🔧</div>
              <div class="area-info">
                <div class="area-name">Mantenimiento</div>
                <div class="area-tickets">{{ ticketsMantenimiento }}</div>
                <div class="area-percentage">{{ porcentajeMantenimiento }}%</div>
                <div class="area-daily-indicator">
                  <span class="daily-label">📅 Promedio Diario:</span>
                  <span class="daily-value">{{ ticketsMantenimientoDiario }} tickets/día</span>
                  <span :class="['daily-trend', getTrendClass(ticketsMantenimientoTendencia)]">
                    {{ getTrendArrow(ticketsMantenimientoTendencia) }}
                    {{ Math.abs(ticketsMantenimientoTendencia) }}%
                  </span>
                </div>
              </div>
            </div>
          </div>


          <!-- Gráfico de barras horizontales por mes -->
          <div class="area-monthly-bars-chart">
            <h4>📊 Tickets por Área - Últimos 6 Meses</h4>
            <div class="monthly-chart-container">
              <div v-for="mes in ticketsPorMes" :key="mes.mes" class="monthly-bar-group">
                <div class="monthly-label">{{ mes.mes }}</div>
                <div class="monthly-bars">
                  <div class="monthly-bar-item">
                    <div class="monthly-bar-fill biomedica" :style="{ width: (mes.biomedica / mes.total * 100) + '%' }">
                      <span v-if="mes.biomedica > 0" class="monthly-bar-value">{{ mes.biomedica }}</span>
                  </div>
                </div>
                  <div class="monthly-bar-item">
                    <div class="monthly-bar-fill tecnologias" :style="{ width: (mes.tecnologias / mes.total * 100) + '%' }">
                      <span v-if="mes.tecnologias > 0" class="monthly-bar-value">{{ mes.tecnologias }}</span>
              </div>
                  </div>
                  <div class="monthly-bar-item">
                    <div class="monthly-bar-fill mantenimiento" :style="{ width: (mes.mantenimiento / mes.total * 100) + '%' }">
                      <span v-if="mes.mantenimiento > 0" class="monthly-bar-value">{{ mes.mantenimiento }}</span>
                </div>
              </div>
                  </div>
                <div class="monthly-total">Total: {{ mes.total }}</div>
                </div>
              </div>
            <div class="monthly-chart-legend">
              <div class="legend-item">
                <div class="legend-color biomedica"></div>
                <span>Biomédica</span>
              </div>
              <div class="legend-item">
                <div class="legend-color tecnologias"></div>
                <span>Tecnologías</span>
              </div>
              <div class="legend-item">
                <div class="legend-color mantenimiento"></div>
                <span>Mantenimiento</span>
              </div>
            </div>
          </div>

          <!-- Tabla detallada -->
          <div class="area-table">
            <table>
              <thead>
                <tr>
                  <th>Área</th>
                  <th>Total Tickets</th>
                  <th>Resueltos</th>
                  <th>Pendientes</th>
                  <th>Tiempo Promedio</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="area in areasDetalladas" :key="area.nombre">
                  <td class="area-name-cell">
                    <span :class="['area-icon-small', area.clase]">{{ area.icono }}</span>
                    {{ area.nombre }}
                  </td>
                  <td>{{ area.total }}</td>
                  <td>{{ area.resueltos }}</td>
                  <td>{{ area.pendientes }}</td>
                  <td>{{ area.tiempoPromedio }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div class="stats-actions">
          <button @click="cerrarTicketsPorArea">Cerrar</button>
        </div>
      </div>
    </div>

    <!-- Vista 5: Problemas más comunes -->
    <div v-if="showProblemasMasComunes" class="stats-view">
      <div class="stats-container">
        <div class="stats-header">
          <h2> Panel de Problemas Más Comunes</h2>
          <button class="close-btn" @click="cerrarProblemasMasComunes">✕</button>
        </div>
        <div class="stats-content">
          <!-- Filtros -->
          <div class="date-filters">
            <div class="date-filter-group">
              <label for="filtroMinimoProblemas">Mínimo de tickets:</label>
              <input 
                type="number" 
                id="filtroMinimoProblemas" 
                v-model.number="filtroMinimoProblemas" 
                min="1"
                @change="analizarProblemas"
                class="date-input"
              />
            </div>
            <div class="date-filter-group">
              <label for="filtroTopN">Mostrar top:</label>
              <select 
                id="filtroTopN" 
                v-model.number="filtroTopN" 
                @change="analizarProblemas"
                class="year-select"
              >
                <option :value="10">Top 10</option>
                <option :value="20">Top 20</option>
                <option :value="50">Top 50</option>
                <option :value="100">Top 100</option>
                <option :value="0">Todos</option>
              </select>
            </div>
            <button @click="analizarProblemas" class="clear-filters-btn">
              🔄 Actualizar Análisis
            </button>
          </div>

          <!-- Resumen -->
          <div class="stats-summary">
            <div class="stat-card">
              <div class="stat-number">{{ problemasAnalizados.length }}</div>
              <div class="stat-label">Problemas Identificados</div>
            </div>
            <div class="stat-card">
              <div class="stat-number">{{ totalTicketsAnalizados }}</div>
              <div class="stat-label">Tickets Analizados</div>
            </div>
            <div class="stat-card">
              <div class="stat-number">{{ ticketsConProblemaIdentificado }}</div>
              <div class="stat-label">Tickets con Problema Identificado</div>
            </div>
            <div class="stat-card">
              <div class="stat-number">{{ porcentajeCobertura }}%</div>
              <div class="stat-label">Cobertura de Análisis</div>
            </div>
          </div>

          <!-- Ranking de problemas -->
          <div class="problemas-ranking">
            <h4>Ranking de Problemas Más Comunes</h4>
            <div class="problemas-list">
              <div 
                v-for="(problema, index) in problemasRanking" 
                :key="index"
                class="problema-item"
                :class="getRankingClass(index)"
              >
                <div class="problema-rank">
                  <span class="rank-number">#{{ index + 1 }}</span>
                  <span class="rank-badge" :class="getRankingBadgeClass(index)">
                    {{ getRankingBadgeText(index) }}
                  </span>
                </div>
                <div class="problema-content">
                  <div class="problema-titulo">
                    <span class="problema-icon">{{ getProblemaIcon(problema.categoria) }}</span>
                    <strong>{{ problema.nombre }}</strong>
                    <span class="problema-categoria">{{ problema.categoria }}</span>
                  </div>
                  <div class="problema-descripcion">
                    <span class="problema-sinonimos">
                      Términos relacionados: {{ problema.terminosRelacionados.join(', ') }}
                    </span>
                  </div>
                  <div class="problema-stats">
                    <div class="problema-bar-container">
                      <div 
                        class="problema-bar-fill" 
                        :style="{ width: problema.porcentaje + '%' }"
                      ></div>
                      <span class="problema-count">{{ problema.cantidad }} tickets</span>
                      <span class="problema-percentage">{{ problema.porcentaje }}%</span>
                    </div>
                  </div>
                  <div class="problema-ejemplos" v-if="problema.ejemplos && problema.ejemplos.length > 0">
                    <details>
                      <summary>Ver ejemplos de descripciones ({{ problema.ejemplos.length }})</summary>
                      <ul class="ejemplos-list">
                        <li v-for="(ejemplo, idx) in problema.ejemplos.slice(0, 5)" :key="idx">
                          "{{ ejemplo }}"
                        </li>
                      </ul>
                    </details>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Gráfico de barras -->
          <div class="problemas-chart">
            <h4> Distribución de Problemas</h4>
            <div class="problemas-bars">
              <div 
                v-for="(problema, index) in problemasRanking.slice(0, 10)" 
                :key="index"
                class="problema-bar-item"
              >
                <div class="problema-bar-label">{{ problema.nombre }}</div>
                <div class="problema-bar-wrapper">
                  <div 
                    class="problema-bar" 
                    :style="{ width: (problema.cantidad / maxCantidad * 100) + '%' }"
                  >
                    <span class="problema-bar-value">{{ problema.cantidad }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="stats-actions">
          <button @click="cerrarProblemasMasComunes">Cerrar</button>
        </div>
      </div>
    </div>
    </div>
</template>

<script>
import { analizarProblemas as analizarProblemasUtil } from '@/utils/analisisProblemasTickets.js';

export default {
    name: 'TicketStatsModal',
  props: {
    showTicketsCreadosResueltos: {
      type: Boolean,
      default: false
    },
    showTiempoPromedio: {
      type: Boolean,
      default: false
    },
    showTicketsPorTecnico: {
      type: Boolean,
      default: false
    },
    showTicketsPorArea: {
      type: Boolean,
      default: false
    },
    showProblemasMasComunes: {
      type: Boolean,
      default: false
    },
    tickets: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      // Modal 1: Tickets creados y resueltos
      periodoActivo: 'dia',
      periodos: [
        { id: 'dia', nombre: 'Por Día' },
        { id: 'semana', nombre: 'Por Semana' },
        { id: 'año', nombre: 'Por Año' }
      ],
      // Filtros de fecha
      fechaInicio: '',
      fechaFin: '',
      añoSeleccionado: '',
      mesSeleccionado: '',
      añosDisponibles: [2023, 2024, 2025],
      meses: [
        'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
        'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
      ],
      ticketsCreados: 0,
      ticketsResueltos: 0,
      ticketsPendientes: 0,
      tasaResolucion: 0,
      datosGrafico: [],

      // Modal 2: Tiempo promedio de resolución
      tiempoPromedioGeneral: '0 horas',
      tiempoPromedioHoras: 0,
      totalTicketsProcesados: 0,
      tiempoAlta: '0 horas',
      ticketsAlta: 0,
      tiempoMedia: '0 horas',
      ticketsMedia: 0,
      tiempoBaja: '0 horas',
      ticketsBaja: 0,
      tiempoPorArea: [],
      tiempoAltaHoras: 0,
      tiempoMediaHoras: 0,
      tiempoBajaHoras: 0,

      // Modal 3: Tickets por técnico
      totalTecnicos: 0,
      totalTicketsAtendidos: 0,
      promedioPorTecnico: 0,
      tecnicosData: [],

      // Modal 4: Tickets por área
      ticketsBiomedica: 0,
      ticketsTecnologias: 0,
      ticketsMantenimiento: 0,
      porcentajeBiomedica: 0,
      porcentajeTecnologias: 0,
      porcentajeMantenimiento: 0,
      
      // Indicadores diarios por área
      ticketsBiomedicaDiario: 0,
      ticketsTecnologiasDiario: 0,
      ticketsMantenimientoDiario: 0,
      ticketsBiomedicaTendencia: 0,
      ticketsTecnologiasTendencia: 0,
      ticketsMantenimientoTendencia: 0,
      
      // Datos mensuales por área (últimos 6 meses)
      ticketsPorMes: [],
      
      areasDetalladas: [],

      // Modal 5: Problemas más comunes
      filtroMinimoProblemas: 2,
      filtroTopN: 20,
      problemasAnalizados: [],
      problemasRanking: []
    }
  },
  computed: {
    periodoActivoTexto() {
      const periodo = this.periodos.find(p => p.id === this.periodoActivo);
      return periodo ? periodo.nombre : 'Por Día';
    },
    datosGraficoNormalizados() {
      if (!Array.isArray(this.datosGrafico) || this.datosGrafico.length === 0) {
        return [];
      }

      const valores = this.datosGrafico.map(item => Number(item.valor) || 0);
      const maxValor = valores.length ? Math.max(...valores) : 0;
      const divisor = maxValor > 0 ? maxValor : 1;
      const alturaMinima = 18;

      return this.datosGrafico.map(item => {
        const valor = Number(item.valor) || 0;
        const altura = valor > 0 ? Math.max(alturaMinima, (valor / divisor) * 100) : 0;
        return {
          ...item,
          valor,
          altura
        };
      });
    },
    prioridadChart() {
      const valores = [
        {
          label: 'Alta',
          horas: this.tiempoAltaHoras || 0,
          valorTexto: this.tiempoAlta,
          className: 'high',
          tickets: this.ticketsAlta || 0
        },
        {
          label: 'Media',
          horas: this.tiempoMediaHoras || 0,
          valorTexto: this.tiempoMedia,
          className: 'medium',
          tickets: this.ticketsMedia || 0
        },
        {
          label: 'Baja',
          horas: this.tiempoBajaHoras || 0,
          valorTexto: this.tiempoBaja,
          className: 'low',
          tickets: this.ticketsBaja || 0
        }
      ];
      const maxHoras = Math.max(...valores.map(v => v.horas), 0);
      const divisor = maxHoras > 0 ? maxHoras : 1;
      return valores.map(v => ({
        ...v,
        height: v.horas > 0
          ? Math.min(100, Math.max(10, (v.horas / divisor) * 100))
          : v.tickets > 0
            ? 10
            : 0
      }));
    },
    totalTicketsAnalizados() {
      return this.tickets ? this.tickets.length : 0;
    },
    ticketsConProblemaIdentificado() {
      return this.problemasRanking.reduce((sum, p) => sum + p.cantidad, 0);
    },
    porcentajeCobertura() {
      if (this.totalTicketsAnalizados === 0) return 0;
      return ((this.ticketsConProblemaIdentificado / this.totalTicketsAnalizados) * 100).toFixed(1);
    },
    maxCantidad() {
      if (this.problemasRanking.length === 0) return 1;
      return Math.max(...this.problemasRanking.map(p => p.cantidad));
    }
  },
  watch: {
    // Cuando se abre el modal de tickets creados/resueltos, cargar datos
    showTicketsCreadosResueltos(newVal) {
      if (newVal) {
        this.cargarEstadisticasDesdeBackend();
      }
    },
    // Cuando se abre el modal de tiempo promedio, cargar datos
    showTiempoPromedio(newVal) {
      if (newVal) {
        this.cargarEstadisticasDesdeBackend();
      }
    },
    // Cuando se abre el modal de tickets por técnico, cargar datos
    showTicketsPorTecnico(newVal) {
      if (newVal) {
        this.cargarEstadisticasTecnicosDesdeBackend();
      }
    },
    // Cuando se abre el modal de tickets por área, cargar datos
    showTicketsPorArea(newVal) {
      if (newVal) {
        this.cargarEstadisticasDesdeBackend();
      }
    },
    // Cuando se abre el modal de problemas más comunes, analizar
    showProblemasMasComunes(newVal) {
      console.log('🔄 showProblemasMasComunes watch:', newVal);
      if (newVal) {
        console.log('✅ Abriendo panel de problemas más comunes');
        this.$nextTick(() => {
          this.analizarProblemas();
        });
      }
    },
    // Watch para tickets: si cambian y el panel está abierto, re-analizar
    tickets: {
      handler(newTickets, oldTickets) {
        console.log('🔄 Tickets prop cambió:', {
          antes: oldTickets?.length || 0,
          ahora: newTickets?.length || 0
        });
        if (this.showProblemasMasComunes && newTickets && newTickets.length > 0) {
          console.log('🔄 Re-analizando problemas porque los tickets cambiaron');
          this.$nextTick(() => {
            this.analizarProblemas();
          });
        }
      },
      deep: true,
      immediate: false
    }
  },
  mounted() {
    console.log('TicketStatsModal mounted');
    console.log(' Tickets prop recibido:', this.tickets?.length || 0, 'tickets');
  },
  methods: {
    // Métodos para cerrar modales
    cerrarTicketsCreadosResueltos() {
      this.$emit('cerrar-tickets-creados-resueltos');
    },
    
    cerrarTiempoPromedio() {
      this.$emit('cerrar-tiempo-promedio');
    },
    
    cerrarTicketsPorTecnico() {
      this.$emit('cerrar-tickets-por-tecnico');
    },
    
    cerrarTicketsPorArea() {
      this.$emit('cerrar-tickets-por-area');
    },
    
    cerrarProblemasMasComunes() {
      this.$emit('cerrar-problemas-mas-comunes');
    },
    
    //  MÉTODOS DE COMUNICACIÓN CON BACKEND 
    // Cargar estadísticas generales desde el backend
    async cargarEstadisticasDesdeBackend(filtros = {}) {
      try {
        const response = await this.$http({
          url: '/mantenimiento/tickets/estadisticas',
          method: 'GET',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          },
          params: {
            fechaInicio: filtros.fechaInicio || undefined,
            fechaFin: filtros.fechaFin || undefined,
            area: filtros.area || undefined,
            tecnico: filtros.tecnico || undefined
          }
        });
        
        const data = response.data || {};

        if (data && data.error) {
          console.warn('Backend devolvió error de estadísticas:', data.mensaje || data.error);
          this.$emit('mostrar-notificacion', data.mensaje || 'No se pudieron obtener las estadísticas');
          return;
        }

        const stats = data.estadisticas || data;
        const porEstado = this.normalizarMapa(stats.porEstado);
        const porArea = this.normalizarMapa(stats.porArea);
        const porAreaResueltos = this.normalizarMapa(stats.porAreaResueltos);
        const porAreaPendientes = this.normalizarMapa(stats.porAreaPendientes);
        const porPrioridad = this.normalizarMapa(stats.porPrioridad);

        // Totales generales
        this.ticketsCreados = Number(stats.totalTickets ?? stats.ticketsCreados) || 0;
        const ticketsResueltos = this.obtenerValorMapa(porEstado, ['RESUELTO', 'CERRADO']) || Number(stats.ticketsResueltos) || 0;
        this.ticketsResueltos = ticketsResueltos;
        const pendientesCalculados = this.obtenerValorMapa(porEstado, ['PENDIENTE', 'EN_PROCESO', 'ASIGNADO', 'PENDIENTES']);
        const pendientes = pendientesCalculados || Math.max(this.ticketsCreados - ticketsResueltos, 0);
        this.ticketsPendientes = pendientes;
        this.tasaResolucion = this.calcularTasaResolucion(this.ticketsResueltos, this.ticketsCreados);
        this.actualizarDatosGraficoBasico(porEstado);

        const tiempoPromedio = stats.tiempoPromedioResolucion || stats.tiempoPromedio || {};
        this.tiempoPromedioGeneral = this.formatearHoras(tiempoPromedio.horas);
        this.tiempoPromedioHoras = Number(stats.tiempoPromedioHoras ?? tiempoPromedio.horas ?? 0);
        this.totalTicketsProcesados = Number(stats.totalTicketsProcesados ?? stats.ticketsResueltos ?? this.ticketsResueltos) || 0;
          
        // Datos por prioridad
        this.ticketsAlta = this.obtenerValorMapa(porPrioridad, ['ALTA']);
        this.ticketsMedia = this.obtenerValorMapa(porPrioridad, ['MEDIA']);
        this.ticketsBaja = this.obtenerValorMapa(porPrioridad, ['BAJA']);

        this.tiempoAltaHoras = Number(stats.tiempoAlta ?? 0) || 0;
        this.tiempoMediaHoras = Number(stats.tiempoMedia ?? 0) || 0;
        this.tiempoBajaHoras = Number(stats.tiempoBaja ?? 0) || 0;

        this.tiempoAlta = this.formatearHoras(this.tiempoAltaHoras);
        this.tiempoMedia = this.formatearHoras(this.tiempoMediaHoras);
        this.tiempoBaja = this.formatearHoras(this.tiempoBajaHoras);

        // Datos por área
        this.ticketsBiomedica = this.obtenerValorMapa(porArea, ['BIOMEDICA', 'BIOMÉDICA']);
        this.ticketsTecnologias = this.obtenerValorMapa(porArea, ['TECNOLOGIAS', 'TECNOLOGÍAS', 'TECNOLOGIA', 'TECNOLOGÍA']);
        this.ticketsMantenimiento = this.obtenerValorMapa(porArea, ['MANTENIMIENTO']);
            
        const totalAreas = this.ticketsBiomedica + this.ticketsTecnologias + this.ticketsMantenimiento;
        this.porcentajeBiomedica = this.calcularPorcentaje(this.ticketsBiomedica, totalAreas);
        this.porcentajeTecnologias = this.calcularPorcentaje(this.ticketsTecnologias, totalAreas);
        this.porcentajeMantenimiento = this.calcularPorcentaje(this.ticketsMantenimiento, totalAreas);

            await this.calcularIndicadoresDiarios(filtros);

        const tiempoPorAreaMap = this.normalizarMapaGenerico(stats.tiempoPorArea);
        this.tiempoPorArea = Object.keys(tiempoPorAreaMap).map(areaKey => {
          const info = tiempoPorAreaMap[areaKey] || {};
          return {
            nombre: this.formatearNombreArea(areaKey),
            tiempo: this.obtenerTiempoDesdeObjeto(info),
            porcentaje: Number(info.porcentaje) || 0
          };
        });

        this.actualizarAreasDetalladas({
          ...stats,
          porArea,
          porAreaResueltos,
          porAreaPendientes,
          porEstado,
          porPrioridad,
          tiempoPorArea: tiempoPorAreaMap
        });

          if (this.showTicketsPorArea) {
            await this.generarDatosGraficaMensualAreas();
          }
          
          console.log(' Estadísticas cargadas desde el backend');
      } catch (error) {
        console.error('Error al cargar estadísticas desde backend:', error);
        this.$emit('mostrar-notificacion', ' Error al cargar estadísticas');
      }
    },
    
    // Cargar estadísticas de técnicos desde el backend
    async cargarEstadisticasTecnicosDesdeBackend() {
      try {
        const response = await this.$http({
          url: '/mantenimiento/tickets/listarTecnicos',
          method: 'GET'
        });
        
        if (response.data && response.data.root) {
          this.totalTecnicos = response.data.root.length;
          
          // Obtener estadísticas de cada técnico
          const tecnicosConStats = [];
          let totalAtendidos = 0;
          
          for (const tecnico of response.data.root) {
            try {
              // Obtener estadísticas individuales del técnico
              const statsResponse = await this.$http({
                url: `/mantenimiento/tickets/estadisticasTecnico/${tecnico.id}`,
                method: 'GET',
                headers: {
                  'Cache-Control': 'no-cache, no-store, must-revalidate',
                  'Pragma': 'no-cache',
                  'Expires': '0'
                }
              });
              
              const data = statsResponse.data || {};

              if ((data.success === false) || data.error) {
                console.warn(` Backend devolvió error de estadísticas para técnico ${tecnico.id}:`, data.mensaje || data.error);
                continue;
              }

              const stats = data.root || data.estadisticas || data;
                
                const iniciales = (tecnico.nombre || tecnico.nombres || '')
                  .split(' ')
                  .map(n => n.charAt(0))
                  .slice(0, 2)
                  .join('');
                
              const atendidos = Number(
                stats.ticketsAtendidos ??
                stats.ticketsAsignados ??
                stats.total ??
                0
              ) || 0;

              const resueltos = Number(
                stats.ticketsResueltos ??
                stats.resueltos ??
                stats.ticketsCerrados ??
                0
              ) || 0;

              const pendientes = Number(
                stats.ticketsPendientes ??
                stats.pendientes ??
                (atendidos - resueltos) ??
                0
              ) || 0;

              const eficienciaCalculada = this.calcularTasaResolucion(resueltos, atendidos);
              const eficiencia = Number(
                stats.eficiencia ??
                stats.tasaResolucion ??
                eficienciaCalculada
              ) || 0;

              const tiempoPromedio = this.formatearHoras(
                stats.tiempoPromedio ??
                stats.tiempoPromedioResolucion ??
                stats.promedioHoras ??
                stats.promedioTiempo
              );
                
                tecnicosConStats.push({
                  id: tecnico.id,
                  nombre: tecnico.nombre || `${tecnico.nombres} ${tecnico.apellidoPaterno || ''}`.trim(),
                  iniciales: iniciales,
                atendidos,
                resueltos,
                pendientes,
                tiempoPromedio,
                eficiencia: Number(eficiencia.toFixed ? eficiencia.toFixed(1) : eficiencia)
                });
                
                totalAtendidos += atendidos;
            } catch (error) {
              console.warn(`No se pudieron cargar estadísticas del técnico ${tecnico.id}`, error);
            }
          }
          
          this.tecnicosData = tecnicosConStats;
          this.totalTicketsAtendidos = totalAtendidos;
          this.promedioPorTecnico = this.totalTecnicos > 0 
            ? (totalAtendidos / this.totalTecnicos).toFixed(1) 
            : 0;
          
          console.log(` Estadísticas de ${tecnicosConStats.length} técnicos cargadas`);
        }
      } catch (error) {
        console.error('Error al cargar técnicos:', error);
        this.$emit('mostrar-notificacion', ' Error al cargar estadísticas de técnicos');
      }
    },
    
    // Método para cambiar período
    cambiarPeriodo(periodoId) {
      this.periodoActivo = periodoId;
      // Aquí se actualizarían los datos según el período seleccionado
      this.actualizarDatosPorPeriodo();
    },
    
    async actualizarDatosPorPeriodo() {
      // Cargar datos desde el backend según el período seleccionado
      await this.cargarEstadisticasDesdeBackend({
        periodo: this.periodoActivo
      });
    },
    
    // Método para obtener clase de eficiencia
    getEfficiencyClass(eficiencia) {
      if (eficiencia >= 90) return 'high';
      if (eficiencia >= 75) return 'medium';
      return 'low';
    },
    
    // Método para obtener clase de tendencia
    getTrendClass(tendencia) {
      if (tendencia > 0) return 'trend-up';    // Azul = aumentó
      if (tendencia < 0) return 'trend-down';  // Rojo = disminuyó
      return 'trend-neutral';                  // Gris = sin cambio
    },
    
    // Método para obtener símbolo de flecha de tendencia
    getTrendArrow(tendencia) {
      if (tendencia > 0) return '↑';
      if (tendencia < 0) return '↓';
      return '→';
    },
    
    // Métodos para filtros de fecha
    async actualizarDatosPorFecha() {
      if (this.fechaInicio && this.fechaFin) {
        console.log('Filtrando por rango de fechas:', this.fechaInicio, 'a', this.fechaFin);
        await this.actualizarEstadisticasPorFecha();
      }
    },
    
    async actualizarDatosPorAño() {
      if (this.añoSeleccionado) {
        console.log('Filtrando por año:', this.añoSeleccionado);
        // Limpiar mes seleccionado cuando cambia el año
        this.mesSeleccionado = '';
        await this.actualizarEstadisticasPorAño();
      }
    },
    
    async actualizarDatosPorMes() {
      if (this.añoSeleccionado && this.mesSeleccionado) {
        console.log('Filtrando por año y mes:', this.añoSeleccionado, this.mesSeleccionado);
        await this.actualizarEstadisticasPorMes();
      }
    },
    
    limpiarFiltros() {
      this.fechaInicio = '';
      this.fechaFin = '';
      this.añoSeleccionado = '';
      this.mesSeleccionado = '';
      this.periodoActivo = 'dia';
      // Restaurar datos originales
      this.actualizarDatosPorPeriodo();
    },
    
    async actualizarEstadisticasPorFecha() {
      // Cargar datos desde el backend según el rango de fechas
      await this.cargarEstadisticasDesdeBackend({
        fechaInicio: this.fechaInicio,
        fechaFin: this.fechaFin
      });
    },
    
    async actualizarEstadisticasPorAño() {
      // Calcular fechas del año seleccionado
      const fechaInicio = `${this.añoSeleccionado}-01-01`;
      const fechaFin = `${this.añoSeleccionado}-12-31`;
      
      await this.cargarEstadisticasDesdeBackend({
        fechaInicio: fechaInicio,
        fechaFin: fechaFin
      });
    },
    
    async actualizarEstadisticasPorMes() {
      // Calcular fechas del mes seleccionado
      const año = this.añoSeleccionado;
      const mes = String(this.mesSeleccionado).padStart(2, '0');
      
      // Primer día del mes
      const fechaInicio = `${año}-${mes}-01`;
      
      // Último día del mes
      const ultimoDia = new Date(año, this.mesSeleccionado, 0).getDate();
      const fechaFin = `${año}-${mes}-${ultimoDia}`;
      
      await this.cargarEstadisticasDesdeBackend({
        fechaInicio: fechaInicio,
        fechaFin: fechaFin
      });
    },
    
    generarDatosGraficoPorFecha() {
      // Los datos ya vienen del backend en cargarEstadisticasDesdeBackend
      console.log('Datos de gráfico actualizados desde backend');
    },
    
    generarDatosGraficoPorMes() {
      // Los datos ya vienen del backend en cargarEstadisticasDesdeBackend
      console.log('Datos de gráfico actualizados desde backend');
    },
    
    // Generar datos para gráfica mensual de áreas (últimos 6 meses)
    async generarDatosGraficaMensualAreas() {
      try {
        const meses = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 
                       'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
        
        const ticketsPorMes = [];
        const fechaActual = new Date();
        
        // Obtener datos de los últimos 6 meses
        for (let i = 5; i >= 0; i--) {
          const fecha = new Date(fechaActual.getFullYear(), fechaActual.getMonth() - i, 1);
          const año = fecha.getFullYear();
          const mesNum = fecha.getMonth() + 1;
          const mesNombre = meses[fecha.getMonth()];
          
          // Calcular primer y último día del mes
          const fechaInicio = `${año}-${String(mesNum).padStart(2, '0')}-01`;
          const ultimoDia = new Date(año, mesNum, 0).getDate();
          const fechaFin = `${año}-${String(mesNum).padStart(2, '0')}-${ultimoDia}`;
          
          // Obtener estadísticas del mes
          const response = await this.$http({
            url: '/mantenimiento/tickets/estadisticas',
            method: 'GET',
            headers: {
              'Cache-Control': 'no-cache, no-store, must-revalidate',
              'Pragma': 'no-cache',
              'Expires': '0'
            },
            params: {
              fechaInicio: fechaInicio,
              fechaFin: fechaFin
            }
          });
          
          const data = response.data || {};
          if (!data.error) {
            const stats = data.estadisticas || data;
            const porArea = this.normalizarMapa(stats.porArea);

            const biomedica = this.obtenerValorMapa(porArea, ['BIOMEDICA', 'BIOMÉDICA']);
            const tecnologias = this.obtenerValorMapa(porArea, ['TECNOLOGIAS', 'TECNOLOGÍAS', 'TECNOLOGIA', 'TECNOLOGÍA']);
            const mantenimiento = this.obtenerValorMapa(porArea, ['MANTENIMIENTO']);
            
            ticketsPorMes.push({
              mes: mesNombre,
              biomedica,
              tecnologias,
              mantenimiento,
              total: biomedica + tecnologias + mantenimiento
            });
          } else {
            ticketsPorMes.push({
              mes: mesNombre,
              biomedica: 0,
              tecnologias: 0,
              mantenimiento: 0,
              total: 0
            });
          }
        }
        
        this.ticketsPorMes = ticketsPorMes;
        console.log('✅ Datos mensuales por área cargados');
      } catch (error) {
        console.error('Error al generar datos de gráfica mensual:', error);
      }
    },
    
    // Calcular indicadores diarios y tendencias por área
    async calcularIndicadoresDiarios(filtros) {
      try {
        // Determinar rango de días
        let diasEnRango = 30; // Por defecto último mes
        
        if (filtros.fechaInicio && filtros.fechaFin) {
          const inicio = new Date(filtros.fechaInicio);
          const fin = new Date(filtros.fechaFin);
          diasEnRango = Math.ceil((fin - inicio) / (1000 * 60 * 60 * 24));
        }
        
        if (diasEnRango <= 0) diasEnRango = 1;
        
        // Calcular promedios diarios
        this.ticketsBiomedicaDiario = (this.ticketsBiomedica / diasEnRango).toFixed(1);
        this.ticketsTecnologiasDiario = (this.ticketsTecnologias / diasEnRango).toFixed(1);
        this.ticketsMantenimientoDiario = (this.ticketsMantenimiento / diasEnRango).toFixed(1);
        
        // Calcular tendencias (comparar con periodo anterior)
        // Por simplicidad, usar valores aleatorios o calcular comparando periodos
        // En producción, esto debería comparar con el mismo periodo anterior
        this.ticketsBiomedicaTendencia = 0;
        this.ticketsTecnologiasTendencia = 0;
        this.ticketsMantenimientoTendencia = 0;
        
      } catch (error) {
        console.error('Error al calcular indicadores diarios:', error);
      }
    },
    
    // Actualizar áreas detalladas con datos del backend
    calcularPorcentaje(valor, total) {
      if (!total) {
        return 0;
      }
      const porcentaje = (Number(valor || 0) / Number(total)) * 100;
      return Number.isFinite(porcentaje) ? Number(porcentaje.toFixed(1)) : 0;
    },
    formatearHoras(valor) {
      if (valor === null || typeof valor === 'undefined') {
        return '0 horas';
      }
      const numero = Number(valor);
      if (Number.isFinite(numero)) {
        const formateado = numero % 1 === 0 ? numero : Number(numero.toFixed(2));
        return `${formateado} horas`;
      }
      if (typeof valor === 'string' && valor.trim() !== '') {
        return valor;
      }
      return '0 horas';
    },
    normalizarMapa(mapa = {}) {
      if (!mapa || typeof mapa !== 'object') {
        return {};
      }
      return Object.keys(mapa).reduce((acc, llave) => {
        if (llave === null || typeof llave === 'undefined') {
          return acc;
        }
        const clave = llave.toString().trim().toUpperCase();
        const valor = mapa[llave];
        if (valor !== null && typeof valor === 'object' && !Array.isArray(valor)) {
          acc[clave] = valor;
        } else if (Number.isFinite(Number(valor))) {
          acc[clave] = Number(valor);
        } else {
          acc[clave] = Number(valor) || 0;
        }
        return acc;
      }, {});
    },
    normalizarMapaGenerico(mapa = {}) {
      if (!mapa || typeof mapa !== 'object') {
        return {};
      }
      return Object.keys(mapa).reduce((acc, llave) => {
        if (llave === null || typeof llave === 'undefined') {
          return acc;
        }
        const clave = llave.toString().trim().toUpperCase();
        acc[clave] = mapa[llave];
        return acc;
      }, {});
    },
    obtenerValorMapa(mapa = {}, llaves = []) {
      if (!mapa || typeof mapa !== 'object' || !Array.isArray(llaves)) {
        return 0;
      }
      for (const llave of llaves) {
        if (!llave) {
          continue;
        }
        const clave = llave.toString().trim().toUpperCase();
        if (Object.prototype.hasOwnProperty.call(mapa, clave)) {
          const valor = mapa[clave];
          if (typeof valor === 'number') {
            return valor;
          }
          if (typeof valor === 'string') {
            const numero = Number(valor);
            if (!Number.isNaN(numero)) {
              return numero;
            }
          }
          if (valor && typeof valor === 'object') {
            if (typeof valor.cantidad !== 'undefined') {
              return Number(valor.cantidad) || 0;
            }
            if (typeof valor.total !== 'undefined') {
              return Number(valor.total) || 0;
            }
            if (typeof valor.valor !== 'undefined') {
              return Number(valor.valor) || 0;
            }
          }
        }
      }
      return 0;
    },
    obtenerObjetoMapa(mapa = {}, llaves = []) {
      if (!mapa || typeof mapa !== 'object' || !Array.isArray(llaves)) {
        return null;
      }
      for (const llave of llaves) {
        if (!llave) {
          continue;
        }
        const clave = llave.toString().trim().toUpperCase();
        if (Object.prototype.hasOwnProperty.call(mapa, clave)) {
          return mapa[clave];
        }
      }
      return null;
    },
    obtenerTiempoDesdeObjeto(info) {
      if (!info || typeof info !== 'object') {
        return '0 horas';
      }
      if (info.tiempo) {
        return info.tiempo;
      }
      if (typeof info.horas !== 'undefined') {
        return this.formatearHoras(info.horas);
      }
      if (typeof info.promedio !== 'undefined') {
        return this.formatearHoras(info.promedio);
      }
      return '0 horas';
    },
    calcularTasaResolucion(resueltos, total) {
      if (!total) {
        return 0;
      }
      const porcentaje = (Number(resueltos || 0) / Number(total)) * 100;
      return Number.isFinite(porcentaje) ? Number(porcentaje.toFixed(1)) : 0;
    },
    actualizarDatosGraficoBasico(porEstado = {}) {
      const total = Number(this.ticketsCreados) || 0;
      const resueltos = this.obtenerValorMapa(porEstado, ['RESUELTO', 'CERRADO']) || Number(this.ticketsResueltos) || 0;
      const pendientesMapa = this.obtenerValorMapa(porEstado, ['PENDIENTE', 'PENDIENTES', 'EN_PROCESO', 'ASIGNADO']);
      const pendientes = pendientesMapa || Math.max(total - (resueltos || 0), 0);

      this.datosGrafico = [
        {
          etiqueta: 'Creados',
          valor: total,
          className: 'created',
          detalles: [
            `Tickets creados: ${total}`
          ]
        },
        {
          etiqueta: 'Resueltos',
          valor: resueltos,
          className: 'resolved',
          detalles: [
            `Tickets resueltos: ${resueltos}`,
            `Efectividad: ${this.tasaResolucion}%`
          ]
        },
        {
          etiqueta: 'Pendientes',
          valor: pendientes,
          className: 'pending',
          detalles: [
            `Tickets pendientes: ${pendientes}`
          ]
        }
      ];
    },
    formatearNombreArea(clave) {
      if (!clave) {
        return '';
      }
      const mapa = {
        BIOMEDICA: 'Biomédica',
        'BIOMÉDICA': 'Biomédica',
        TECNOLOGIAS: 'Tecnologías',
        'TECNOLOGÍAS': 'Tecnologías',
        TECNOLOGIA: 'Tecnología',
        'TECNOLOGÍA': 'Tecnología',
        MANTENIMIENTO: 'Mantenimiento'
      };
      const claveNormalizada = clave.toString().trim().toUpperCase();
      if (mapa[claveNormalizada]) {
        return mapa[claveNormalizada];
      }
      return claveNormalizada.charAt(0) + claveNormalizada.slice(1).toLowerCase();
    },
    actualizarAreasDetalladas(stats) {
      if (!stats) {
        this.areasDetalladas = [];
        return;
      }

      const porArea = this.normalizarMapa(stats.porArea);
      const porAreaResueltos = this.normalizarMapa(stats.porAreaResueltos || stats.porAreaResuelto);
      const porAreaPendientes = this.normalizarMapa(stats.porAreaPendientes || stats.porAreaPendiente);
      const tiempoPorArea = this.normalizarMapaGenerico(stats.tiempoPorArea);

      const areasReferencia = [
        { keys: ['BIOMEDICA', 'BIOMÉDICA'], icono: '🔬', clase: 'biomedica' },
        { keys: ['TECNOLOGIAS', 'TECNOLOGÍAS', 'TECNOLOGIA', 'TECNOLOGÍA'], icono: '💻', clase: 'tecnologias' },
        { keys: ['MANTENIMIENTO'], icono: '🔧', clase: 'mantenimiento' }
      ];

      this.areasDetalladas = areasReferencia.map(({ keys, icono, clase }) => {
        const nombre = this.formatearNombreArea(keys[0]);
        const tiempoInfo = this.obtenerObjetoMapa(tiempoPorArea, keys);

        return {
          nombre,
          icono,
          clase,
          total: this.obtenerValorMapa(porArea, keys),
          resueltos: this.obtenerValorMapa(porAreaResueltos, keys),
          pendientes: this.obtenerValorMapa(porAreaPendientes, keys),
          tiempoPromedio: this.obtenerTiempoDesdeObjeto(tiempoInfo)
        };
      });
    },

    // ========== MÉTODOS PARA ANÁLISIS DE PROBLEMAS MÁS COMUNES ==========
    
    // Analizar problemas de los tickets usando la utilidad
    analizarProblemas() {
      console.log(' analizarProblemas() ejecutado');
      console.log(' Tickets recibidos:', this.tickets?.length || 0, 'tickets');
      console.log(' Tickets data:', this.tickets);
      
      const ticketsArray = this.tickets || [];
      const resultado = analizarProblemasUtil(
        ticketsArray,
        this.filtroMinimoProblemas,
        this.filtroTopN
      );

      console.log(' Resultado del análisis:', resultado);
      this.problemasAnalizados = resultado.problemasAnalizados;
      this.problemasRanking = resultado.problemasRanking;
      console.log(' Problemas analizados:', this.problemasAnalizados.length);
      console.log(' Ranking:', this.problemasRanking.length);
    },

    // Obtener clase CSS para ranking
    getRankingClass(index) {
      if (index === 0) return 'rank-gold';
      if (index === 1) return 'rank-silver';
      if (index === 2) return 'rank-bronze';
      return '';
    },

    // Obtener clase de badge para ranking
    getRankingBadgeClass(index) {
      if (index === 0) return 'badge-gold';
      if (index === 1) return 'badge-silver';
      if (index === 2) return 'badge-bronze';
      return 'badge-normal';
    },

    // Obtener texto de badge
    getRankingBadgeText(index) {
      if (index === 0) return '🔴 Más crítico';
      if (index === 1) return '🟡 Segundo más frecuente';
      if (index === 2) return '🟠 Tercero más frecuente';
      return '';
    },

    // Obtener icono según categoría
    getProblemaIcon(categoria) {
      const iconos = {
        'Hardware': '💻',
        'Software': '📱',
        'Red': '🌐',
        'Periférico': '🖨️',
        'Rendimiento': '⚡',
        'Error': '⚠️',
        'General': '📋'
      };
      return iconos[categoria] || '📋';
    }
  }
}
</script>

<style scoped>
/* Estilos para dashboard de estadísticas */
.stats-dashboard {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  /* Paleta institucional sin verdes */
  --color-primary: #1f4f7a;
  --color-primary-dark: #143552;
  --color-navy: #1c2f48;
  --color-slate: #4f6d7a;
  --color-info: #2f6b8a;
  --color-amber: #d17a22;
  --color-amber-dark: #9b5715;
  --color-red: #b33430;
  --color-red-dark: #8a2726;
  --color-bg: #f6f8fa;
  --color-bg-alt: #eef1f5;
  --color-border: #e3e7ed;
  background: linear-gradient(180deg, var(--color-bg) 0%, var(--color-bg-alt) 100%);
  z-index: 1000;
  overflow-y: auto;
  font-family: 'Helvetica LT Std Condensed Oblique', 'Helvetica Neue', Helvetica, Arial, sans-serif;
}

.stats-view {
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 20px;
}

.stats-container {
  background: white;
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  width: 95vw;
  max-width: 1400px;
  margin: 20px auto;
  overflow: hidden;
}

.stats-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 30px 40px;
  border-bottom: 1px solid var(--color-border);
  background: linear-gradient(180deg, #ffffff 0%, var(--color-bg) 100%);
}

.stats-header h2 {
  margin: 0;
  color: #333;
  font-size: 2rem;
  font-weight: 700;
  font-family: 'Helvetica LT Std Black Condensed Oblique', sans-serif;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;
  padding: 5px;
  border-radius: 50%;
  width: 35px;
  height: 35px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.close-btn:hover {
  background: #e9ecef;
  color: #333;
}

.stats-content {
  padding: 40px;
}

/* Filtros de período */
.period-filters {
  display: flex;
  gap: 10px;
  margin-bottom: 25px;
  justify-content: center;
}

.period-btn {
  padding: 8px 16px;
  border: 2px solid #e9ecef;
  background: white;
  border-radius: 20px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s ease;
  color: #666;
}

.period-btn.active {
  background: var(--color-primary);
  border-color: var(--color-primary);
  color: white;
}

.period-btn:hover:not(.active) {
  border-color: var(--color-primary);
  color: var(--color-primary);
}

/* Filtros de fecha */
.date-filters {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
  padding: 25px;
  background: #f8f9fa;
  border-radius: 12px;
  border: 1px solid #e9ecef;
}

.date-filter-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.date-filter-group label {
  font-weight: 600;
  color: #333;
  font-size: 0.9rem;
}

.date-input,
.year-select,
.month-select {
  padding: 10px 12px;
  border: 2px solid var(--color-border);
  border-radius: 8px;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  background: white;
}

.date-input:focus,
.year-select:focus,
.month-select:focus {
  outline: none;
  border-color: var(--color-primary);
  box-shadow: 0 0 0 3px rgba(31, 79, 122, 0.15);
}

.date-input:hover,
.year-select:hover,
.month-select:hover {
  border-color: var(--color-primary);
}

.month-select:disabled {
  background: #f8f9fa;
  color: #6c757d;
  cursor: not-allowed;
}

.clear-filters-btn {
  grid-column: 1 / -1;
  justify-self: center;
  background: var(--color-slate);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  margin-top: 10px;
}

.clear-filters-btn:hover {
  background: #3f5965;
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(79, 109, 122, 0.3);
}

/* Estadísticas principales */
.stats-summary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 30px;
  margin-bottom: 40px;
}

.stat-card {
  padding: 30px;
  border-radius: 16px;
  text-align: center;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  transition: transform 0.3s ease;
}

.stat-card:hover {
  transform: translateY(-3px);
}

/* COLORES SEMÁNTICOS: según el resultado, no el área */
.stat-card.created {
  background: linear-gradient(135deg, var(--color-info) 0%, var(--color-navy) 100%); /* Azul institucional */
  color: white;
}

.stat-card.resolved {
  background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-dark) 100%); /* Azul petróleo */
  color: white;
}

.stat-card.pending {
  background: linear-gradient(135deg, var(--color-amber) 0%, var(--color-amber-dark) 100%); /* Ámbar institucional */
  color: white;
}

.stat-card.rate {
  background: linear-gradient(135deg, #25384d 0%, #1a293a 100%); /* Azul profundo */
  color: white;
}

.stat-number {
  font-size: 3.5rem;
  font-weight: bold;
  margin-bottom: 10px;
}

.stat-label {
  font-size: 0.9rem;
  opacity: 0.9;
}

/* Gráfico de barras */
.chart-container h4 {
  margin: 0 0 20px 0;
  color: #333;
  text-align: center;
  font-size: 1.2rem;
  font-family: 'Helvetica LT Std Condensed Oblique', 'Helvetica Neue', Helvetica, Arial, sans-serif;
}

.bar-chart {
  display: flex;
  align-items: end;
  gap: 15px;
  height: 200px;
  margin-bottom: 20px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
}

.bar-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
}

.bar-label {
  margin-bottom: 10px;
  font-weight: 600;
  color: #333;
  font-size: 0.9rem;
}

.bar-container {
  display: flex;
  flex-direction: column;
  gap: 8px;
  height: 160px;
  width: 100%;
  justify-content: flex-end;
}

.bar-created,
.bar-resolved {
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 0;
  position: relative;
  transition: height 0.35s ease, background 0.3s ease;
  overflow: hidden;
}

.bar-created {
  background: linear-gradient(180deg, var(--color-info) 0%, var(--color-navy) 100%); /* Azul institucional */
}

.bar-resolved {
  background: linear-gradient(180deg, var(--color-primary) 0%, var(--color-primary-dark) 100%); /* Azul petróleo */
}

.bar-created.empty,
.bar-resolved.empty {
  background: rgba(0, 0, 0, 0.08);
}

.bar-value {
  color: white;
  font-weight: bold;
  font-size: 0.8rem;
  padding: 2px 4px;
}

.bar-metrics {
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 0.8rem;
  color: #4a4a4a;
  text-align: center;
}

.bar-metrics .metric {
  display: block;
}

.bar-metrics .metric-created {
  color: var(--color-navy);
  font-weight: 600;
}

.bar-metrics .metric-resolved {
  color: var(--color-primary-dark);
  font-weight: 600;
}

.bar-metrics .metric-total {
  color: #555;
  font-weight: 500;
}

.chart-legend {
  display: flex;
  justify-content: center;
  gap: 20px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.legend-color {
  width: 16px;
  height: 16px;
  border-radius: 3px;
}

.legend-color.created {
  background: #2f6b8a; /* Azul institucional = Creados */
}

.legend-color.resolved {
  background: #1f4f7a; /* Azul petróleo = Resueltos */
}

/* Tiempo promedio */
.time-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
}

.time-card {
  display: flex;
  align-items: center;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
}

.time-card:hover {
  transform: translateY(-5px);
}

/* COLORES SEMÁNTICOS: según rapidez de resolución */
.time-card.general {
  background: linear-gradient(135deg, #7b8e9e 0%, var(--color-slate) 100%); /* Neutro institucional */
  color: white;
}

.time-card.high {
  background: linear-gradient(135deg, var(--color-red) 0%, var(--color-red-dark) 100%); /* Rojo institucional */
  color: white;
}

.time-card.medium {
  background: linear-gradient(135deg, var(--color-amber) 0%, var(--color-amber-dark) 100%); /* Ámbar institucional */
  color: white;
}

.time-card.low {
  background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-dark) 100%); /* Azul petróleo */
  color: white;
}

.time-icon {
  font-size: 2.5rem;
  margin-right: 20px;
}

.time-info {
  flex: 1;
}

.time-label {
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 5px;
}

.time-value {
  font-size: 1.5rem;
  font-weight: bold;
  margin-bottom: 5px;
}

.time-tickets {
  font-size: 0.9rem;
  opacity: 0.9;
}

/* Gráfico de tiempo por área */
.time-chart {
  margin-bottom: 30px;
}

.time-chart h4 {
  margin: 0 0 20px 0;
  color: #333;
  text-align: center;
  font-size: 1.2rem;
}

.time-bars {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.time-bar-item {
  display: flex;
  align-items: center;
  gap: 15px;
}

.time-bar-label {
  min-width: 120px;
  font-weight: 600;
  color: #333;
}

.time-bar-container {
  flex: 1;
  height: 30px;
  background: #e9ecef;
  border-radius: 15px;
  position: relative;
  overflow: hidden;
}

.time-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #7b8e9e 0%, var(--color-slate) 100%); /* Neutro institucional */
  border-radius: 15px;
  transition: width 0.3s ease;
}

.time-bar-value {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  font-weight: 600;
  color: #333;
  font-size: 0.9rem;
}

/* Tabla de técnicos */
.tech-table {
  overflow-x: auto;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.tech-table table {
  width: 100%;
  border-collapse: collapse;
  background: white;
}

.tech-table th {
  background: #f8f9fa;
  padding: 15px 12px;
  text-align: left;
  font-weight: 600;
  color: #333;
  border-bottom: 2px solid #dee2e6;
}

.tech-table td {
  padding: 12px;
  border-bottom: 1px solid #e9ecef;
  color: #555;
}

.tech-table tr:hover {
  background: #f8f9fa;
}

.tech-name {
  display: flex;
  align-items: center;
  gap: 10px;
}

.tech-avatar {
  width: 35px;
  height: 35px;
  border-radius: 50%;
  background: linear-gradient(135deg, #7b8e9e 0%, var(--color-slate) 100%); /* Neutral institucional */
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 0.9rem;
}

.efficiency {
  padding: 4px 8px;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.85rem;
}

.efficiency.high {
  background: #d6e4f0;
  color: #1f4f7a;
}

.efficiency.medium {
  background: #fff3cd;
  color: #856404;
}

.efficiency.low {
  background: #f8d7da;
  color: #721c24;
}

/* Estadísticas por área */
.area-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
}

.area-card {
  display: flex;
  align-items: center;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
}

.area-card:hover {
  transform: translateY(-5px);
}

/* COLORES SEMÁNTICOS: tonos distintos para diferenciar visualmente, no por área específica */
.area-card.biomedica {
  background: linear-gradient(135deg, #3b5f8c 0%, #274b73 100%); /* Azul acero */
  color: white;
}

.area-card.tecnologias {
  background: linear-gradient(135deg, #1c2f48 0%, #111d2c 100%); /* Azul marino */
  color: white;
}

.area-card.mantenimiento {
  background: linear-gradient(135deg, var(--color-amber) 0%, var(--color-amber-dark) 100%); /* Bronce */
  color: white;
}

.area-icon {
  font-size: 2.5rem;
  margin-right: 20px;
}

.area-info {
  flex: 1;
}

.area-name {
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 5px;
}

.area-tickets {
  font-size: 1.8rem;
  font-weight: bold;
  margin-bottom: 5px;
}

.area-percentage {
  font-size: 0.9rem;
  opacity: 0.9;
}

/* Indicador diario en cards de área */
.area-daily-indicator {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.3);
  display: flex;
  flex-direction: column;
  gap: 5px;
  font-size: 0.85rem;
}

.daily-label {
  font-weight: 600;
  opacity: 0.9;
}

.daily-value {
  font-size: 1rem;
  font-weight: bold;
  margin-top: 2px;
}

.daily-trend {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 3px 8px;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.8rem;
  margin-top: 5px;
  width: fit-content;
}

.daily-trend.trend-up {
  background: rgba(31, 79, 122, 0.35); /* Azul transparente = Aumentó */
  color: #1d2d40;
}

.daily-trend.trend-down {
  background: rgba(244, 67, 54, 0.3); /* Rojo transparente = Disminuyó */
  color: #fff;
}

.daily-trend.trend-neutral {
  background: rgba(125, 137, 148, 0.3); /* Gris transparente = Sin cambio */
  color: #25313d;
}

/* Gráfico de barras mensuales por área */
.area-monthly-bars-chart {
  margin: 40px 0;
  padding: 30px;
  background: #f8f9fa;
  border-radius: 12px;
}

.area-monthly-bars-chart h4 {
  margin: 0 0 30px 0;
  color: #333;
  text-align: center;
  font-size: 1.4rem;
  font-weight: 600;
}

.monthly-chart-container {
  display: flex;
  flex-direction: column;
  gap: 25px;
}

.monthly-bar-group {
  display: grid;
  grid-template-columns: 100px 1fr 120px;
  align-items: center;
  gap: 15px;
}

.monthly-label {
  font-weight: 600;
  color: #333;
  font-size: 1rem;
  text-align: right;
}

.monthly-bars {
  display: flex;
  flex-direction: column;
  gap: 5px;
  background: white;
  border-radius: 8px;
  padding: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.monthly-bar-item {
  height: 28px;
  background: #e9ecef;
  border-radius: 6px;
  overflow: hidden;
  position: relative;
}

.monthly-bar-fill {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: width 0.8s ease;
  min-width: 30px;
}

.monthly-bar-fill.biomedica {
  background: linear-gradient(90deg, #3b5f8c 0%, #274b73 100%); /* Azul acero */
}

.monthly-bar-fill.tecnologias {
  background: linear-gradient(90deg, #1c2f48 0%, #111d2c 100%); /* Azul marino */
}

.monthly-bar-fill.mantenimiento {
  background: linear-gradient(90deg, var(--color-amber) 0%, var(--color-amber-dark) 100%); /* Bronce */
}

.monthly-bar-value {
  color: white;
  font-weight: 600;
  font-size: 0.85rem;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

.monthly-total {
  font-weight: 600;
  color: #555;
  font-size: 0.95rem;
  text-align: center;
  padding: 8px 12px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
}

.monthly-chart-legend {
  display: flex;
  justify-content: center;
  gap: 30px;
  margin-top: 25px;
  padding-top: 20px;
  border-top: 2px solid #dee2e6;
}

.monthly-chart-legend .legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
}

.monthly-chart-legend .legend-color {
  width: 20px;
  height: 20px;
  border-radius: 4px;
}

/* Gráfico circular */
.pie-chart-container {
  margin-bottom: 30px;
}

.pie-chart-container h4 {
  margin: 0 0 20px 0;
  color: #333;
  text-align: center;
  font-size: 1.2rem;
}

.pie-chart {
  width: 200px;
  height: 200px;
  border-radius: 50%;
  background: conic-gradient(
    #1f4f7a 0deg 165.6deg,
    #1c2f48 165.6deg 280.8deg,
    #d17a22 280.8deg 360deg
  );
  margin: 0 auto 20px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pie-chart::before {
  content: '';
  width: 100px;
  height: 100px;
  background: white;
  border-radius: 50%;
  position: absolute;
}

.pie-legend {
  display: flex;
  justify-content: center;
  gap: 20px;
  flex-wrap: wrap;
}

.pie-legend .legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.pie-legend .legend-color {
  width: 16px;
  height: 16px;
  border-radius: 3px;
}

.pie-legend .legend-color.biomedica {
  background: #3b5f8c; /* Azul acero */
}

.pie-legend .legend-color.tecnologias {
  background: #1c2f48; /* Azul marino */
}

.pie-legend .legend-color.mantenimiento {
  background: #d17a22; /* Bronce */
}

/* Tabla de áreas */
.area-table {
  overflow-x: auto;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.area-table table {
  width: 100%;
  border-collapse: collapse;
  background: white;
}

.area-table th {
  background: #f8f9fa;
  padding: 15px 12px;
  text-align: left;
  font-weight: 600;
  color: #333;
  border-bottom: 2px solid #dee2e6;
}

.area-table td {
  padding: 12px;
  border-bottom: 1px solid #e9ecef;
  color: #555;
}

.area-table tr:hover {
  background: #f8f9fa;
}

.area-name-cell {
  display: flex;
  align-items: center;
  gap: 10px;
}

.area-icon-small {
  font-size: 1.2rem;
}

.stats-actions {
  padding: 30px 40px;
  border-top: 1px solid #e0e0e0;
  text-align: right;
  background: #f8f9fa;
}

.stats-actions button {
  background: #6c757d;
  color: white;
  border: none;
  padding: 15px 30px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  font-size: 1.1rem;
  transition: all 0.3s ease;
}

.stats-actions button:hover {
  background: #5a6268;
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
}

.stats-actions button:active {
  transform: translateY(0);
}

/* Estilos para gráficas de barras por prioridad */
.priority-chart {
  margin-bottom: 40px;
}

.priority-chart h4 {
  margin: 0 0 25px 0;
  color: #333;
  text-align: center;
  font-size: 1.4rem;
  font-weight: 600;
}

.priority-bars {
  display: flex;
  justify-content: center;
  align-items: flex-end;
  gap: 30px;
  padding: 10px 0;
}

.priority-bar-item.vertical {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  min-width: 90px;
}

.priority-label {
  font-weight: 600;
  color: #333;
  font-size: 1rem;
}

.priority-bar-box {
  width: 70px;
  height: 200px;
  background: #e9ecef;
  border-radius: 12px;
  position: relative;
  display: flex;
  align-items: flex-end;
  overflow: hidden;
  box-shadow: inset 0 0 8px rgba(0,0,0,0.08);
}

.priority-bar-fill {
  width: 100%;
  border-radius: 12px 12px 0 0;
  transition: height 0.8s ease;
  position: relative;
  min-height: 0;
}

.priority-bar-fill.high {
  background: linear-gradient(180deg, var(--color-red) 0%, var(--color-red-dark) 100%);
}

.priority-bar-fill.medium {
  background: linear-gradient(180deg, var(--color-amber) 0%, var(--color-amber-dark) 100%);
}

.priority-bar-fill.low {
  background: linear-gradient(180deg, var(--color-primary) 0%, var(--color-primary-dark) 100%);
}

.priority-bar-value.vertical {
  font-weight: 600;
  color: #333;
  font-size: 1rem;
}

.priority-bar-meta.vertical {
  font-size: 0.85rem;
  color: #666;
}

/* Estilos para gráficas de barras por técnico */
.tech-chart {
  margin-bottom: 40px;
}

.tech-chart h4 {
  margin: 0 0 25px 0;
  color: #333;
  text-align: center;
  font-size: 1.4rem;
  font-weight: 600;
}

.tech-bars {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.tech-bar-item {
  display: flex;
  align-items: center;
  gap: 20px;
}

.tech-bar-label {
  min-width: 150px;
  font-weight: 600;
  color: #333;
  font-size: 1.1rem;
}

.tech-bar-container {
  flex: 1;
  height: 40px;
  background: #e9ecef;
  border-radius: 20px;
  position: relative;
  overflow: hidden;
}

.tech-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, var(--color-primary) 0%, var(--color-primary-dark) 100%); /* Azul petróleo = Productividad/Rendimiento */
  border-radius: 20px;
  transition: width 0.8s ease;
}

.tech-bar-value {
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  font-weight: 600;
  color: #333;
  font-size: 1rem;
}

/* Estilos para gráficas de barras por área */
.area-bars-chart {
  margin-bottom: 40px;
}

.area-bars-chart h4 {
  margin: 0 0 25px 0;
  color: #333;
  text-align: center;
  font-size: 1.4rem;
  font-weight: 600;
}

.area-bars {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.area-bar-item {
  display: flex;
  align-items: center;
  gap: 20px;
}

.area-bar-label {
  min-width: 150px;
  font-weight: 600;
  color: #333;
  font-size: 1.1rem;
}

.area-bar-container {
  flex: 1;
  height: 40px;
  background: #e9ecef;
  border-radius: 20px;
  position: relative;
  overflow: hidden;
}

.area-bar-fill {
  height: 100%;
  border-radius: 20px;
  transition: width 0.8s ease;
}

.area-bar-fill.biomedica {
  background: linear-gradient(90deg, #3b5f8c 0%, #274b73 100%); /* Azul acero */
}

.area-bar-fill.tecnologias {
  background: linear-gradient(90deg, #1c2f48 0%, #111d2c 100%); /* Azul marino */
}

.area-bar-fill.mantenimiento {
  background: linear-gradient(90deg, var(--color-amber) 0%, var(--color-amber-dark) 100%); /* Bronce */
}

.area-bar-value {
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  font-weight: 600;
  color: #333;
  font-size: 1rem;
}

/* Gráfico de barras verticales por área */
.area-vertical-bars-chart {
  margin-bottom: 40px;
}

.area-vertical-bars-chart h4 {
  margin: 0 0 25px 0;
  color: #333;
  text-align: center;
  font-size: 1.4rem;
  font-weight: 600;
}

.vertical-bars-container {
  display: flex;
  justify-content: space-around;
  align-items: end;
  height: 300px;
  margin-bottom: 25px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 12px;
  border: 1px solid #e9ecef;
}

.vertical-bar-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  max-width: 120px;
}

.vertical-bar-label {
  font-weight: 600;
  color: #333;
  font-size: 1rem;
  margin-bottom: 15px;
  text-align: center;
}

.vertical-bar-wrapper {
  width: 60px;
  height: 200px;
  background: #e9ecef;
  border-radius: 8px 8px 0 0;
  position: relative;
  display: flex;
  align-items: end;
  margin-bottom: 10px;
}

.vertical-bar-fill {
  width: 100%;
  border-radius: 8px 8px 0 0;
  transition: height 0.8s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  min-height: 20px;
}

.vertical-bar-fill.biomedica {
  background: linear-gradient(180deg, #3b5f8c 0%, #274b73 100%); /* Azul acero */
}

.vertical-bar-fill.tecnologias {
  background: linear-gradient(180deg, #1c2f48 0%, #111d2c 100%); /* Azul marino */
}

.vertical-bar-fill.mantenimiento {
  background: linear-gradient(180deg, var(--color-amber) 0%, var(--color-amber-dark) 100%); /* Bronce */
}

.vertical-bar-value {
  color: white;
  font-weight: bold;
  font-size: 0.9rem;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

.vertical-bar-percentage {
  font-weight: 600;
  color: #333;
  font-size: 0.9rem;
  text-align: center;
}

.vertical-chart-legend {
  display: flex;
  justify-content: center;
  gap: 30px;
  flex-wrap: wrap;
}

.vertical-chart-legend .legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.vertical-chart-legend .legend-color {
  width: 16px;
  height: 16px;
  border-radius: 3px;
}

.vertical-chart-legend .legend-color.biomedica {
  background: #3b5f8c; /* Azul acero */
}

.vertical-chart-legend .legend-color.tecnologias {
  background: #1c2f48; /* Azul marino */
}

.vertical-chart-legend .legend-color.mantenimiento {
  background: #d17a22; /* Bronce */
}

/* Responsive para estadísticas */
@media (max-width: 768px) {
  .stats-container {
    width: 98vw;
    margin: 10px;
  }
  
  .stats-header {
    padding: 20px;
  }
  
  .stats-header h2 {
    font-size: 1.5rem;
  }
  
  .stats-content {
    padding: 20px;
  }
  
  .stats-summary {
    grid-template-columns: 1fr;
    gap: 20px;
  }
  
  .time-stats {
    grid-template-columns: 1fr;
  }
  
  .area-stats {
    grid-template-columns: 1fr;
  }
  
  /* Responsive para gráfica mensual */
  .monthly-bar-group {
    grid-template-columns: 80px 1fr 100px;
    gap: 10px;
  }
  
  .monthly-label {
    font-size: 0.9rem;
  }
  
  .monthly-chart-legend {
    flex-direction: column;
    gap: 15px;
    align-items: center;
  }
  
  .bar-chart {
    flex-direction: column;
    height: auto;
    gap: 10px;
  }
  
  .bar-item {
    flex-direction: row;
    height: auto;
  }
  
  .bar-container {
    flex-direction: row;
    height: 30px;
    width: 200px;
  }
  
  .period-filters {
    flex-wrap: wrap;
  }
  
  .date-filters {
    grid-template-columns: 1fr;
    gap: 15px;
    padding: 20px;
  }
  
  .clear-filters-btn {
    grid-column: 1;
    justify-self: stretch;
  }
  
  .vertical-bars-container {
    height: 250px;
    padding: 15px;
  }
  
  .vertical-bar-wrapper {
    width: 50px;
    height: 150px;
  }
  
  .vertical-bar-item {
    max-width: 80px;
  }
  
  .vertical-bar-label {
    font-size: 0.9rem;
  }
  
  .vertical-bar-value {
    font-size: 0.8rem;
  }
  
  .vertical-chart-legend {
    gap: 20px;
  }
  
  .priority-bar-item,
  .tech-bar-item,
  .area-bar-item {
    flex-direction: column;
    gap: 10px;
    align-items: flex-start;
  }
  
  .priority-label,
  .tech-bar-label,
  .area-bar-label {
    min-width: auto;
  }
}

@media (max-width: 480px) {
  .stats-header {
    padding: 15px;
  }
  
  .stats-header h2 {
    font-size: 1.2rem;
  }
  
  .stats-content {
    padding: 15px;
  }
  
  .stat-card {
    padding: 20px;
  }
  
  .stat-number {
    font-size: 2.5rem;
  }
  
  .time-card {
    padding: 15px;
  }
  
  .time-icon {
    font-size: 2rem;
    margin-right: 15px;
  }
  
  .time-value {
    font-size: 1.3rem;
  }
  
  .area-card {
    padding: 15px;
  }
  
  .area-icon {
    font-size: 2rem;
    margin-right: 15px;
  }
  
  .area-tickets {
    font-size: 1.5rem;
  }
  
  .stats-actions {
    padding: 20px 15px;
  }
  
  .stats-actions button {
    padding: 12px 20px;
    font-size: 1rem;
  }
  
  .vertical-bars-container {
    height: 200px;
    padding: 10px;
  }
  
  .vertical-bar-wrapper {
    width: 40px;
    height: 120px;
  }
  
  .vertical-bar-item {
    max-width: 60px;
  }
  
  .vertical-bar-label {
    font-size: 0.8rem;
    margin-bottom: 10px;
  }
  
  .vertical-bar-value {
    font-size: 0.7rem;
  }
  
  .vertical-bar-percentage {
    font-size: 0.8rem;
  }
  
  .vertical-chart-legend {
    flex-direction: column;
    gap: 10px;
    align-items: center;
  }
  
  /* Cards de área más compactas en móvil */
  .area-card {
    padding: 15px;
  }
  
  .area-icon {
    font-size: 2rem;
    margin-right: 15px;
  }
  
  .area-daily-indicator {
    font-size: 0.75rem;
  }
  
  .daily-value {
    font-size: 0.9rem;
  }
  
  /* Gráfica mensual más compacta */
  .area-monthly-bars-chart {
    padding: 20px;
  }
  
  .monthly-bar-group {
    grid-template-columns: 70px 1fr 90px;
  }
  
  .monthly-label {
    font-size: 0.85rem;
  }
  
  .monthly-bar-item {
    height: 24px;
  }
  
  .monthly-bar-value {
    font-size: 0.75rem;
  }
}

/* ========== ESTILOS PARA PROBLEMAS MÁS COMUNES ========== */
.problemas-ranking {
  margin: 30px 0;
}

.problemas-ranking h4 {
  margin: 0 0 25px 0;
  color: #333;
  text-align: center;
  font-size: 1.4rem;
  font-weight: 600;
  font-family: 'Helvetica LT Std Black Condensed Oblique', sans-serif;
}

.problemas-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.problema-item {
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  border-left: 4px solid var(--color-primary);
}

.problema-item:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

.problema-item.rank-gold {
  border-left-color: #ffd700;
  background: linear-gradient(90deg, rgba(255, 215, 0, 0.05) 0%, white 10%);
}

.problema-item.rank-silver {
  border-left-color: #c0c0c0;
  background: linear-gradient(90deg, rgba(192, 192, 192, 0.05) 0%, white 10%);
}

.problema-item.rank-bronze {
  border-left-color: #cd7f32;
  background: linear-gradient(90deg, rgba(205, 127, 50, 0.05) 0%, white 10%);
}

.problema-rank {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 15px;
}

.rank-number {
  font-size: 1.5rem;
  font-weight: bold;
  color: var(--color-primary);
  font-family: 'Helvetica LT Std Black Condensed Oblique', sans-serif;
}

.rank-badge {
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 0.85rem;
  font-weight: 600;
}

.rank-badge.badge-gold {
  background: linear-gradient(135deg, #ffd700 0%, #ffed4e 100%);
  color: #333;
}

.rank-badge.badge-silver {
  background: linear-gradient(135deg, #c0c0c0 0%, #e8e8e8 100%);
  color: #333;
}

.rank-badge.badge-bronze {
  background: linear-gradient(135deg, #cd7f32 0%, #e6a85c 100%);
  color: white;
}

.rank-badge.badge-normal {
  background: var(--color-slate);
  color: white;
}

.problema-content {
  flex: 1;
}

.problema-titulo {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
  flex-wrap: wrap;
}

.problema-titulo strong {
  font-size: 1.2rem;
  color: #333;
  font-family: 'Helvetica LT Std Black Condensed Oblique', sans-serif;
}

.problema-icon {
  font-size: 1.5rem;
}

.problema-categoria {
  padding: 4px 10px;
  background: var(--color-bg);
  border-radius: 8px;
  font-size: 0.85rem;
  color: #666;
  font-weight: 600;
}

.problema-descripcion {
  margin-bottom: 15px;
}

.problema-sinonimos {
  font-size: 0.9rem;
  color: #666;
  font-style: italic;
}

.problema-stats {
  margin: 15px 0;
}

.problema-bar-container {
  position: relative;
  height: 35px;
  background: #e9ecef;
  border-radius: 18px;
  overflow: hidden;
  display: flex;
  align-items: center;
  padding: 0 15px;
}

.problema-bar-fill {
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  background: linear-gradient(90deg, var(--color-primary) 0%, var(--color-primary-dark) 100%);
  border-radius: 18px;
  transition: width 0.8s ease;
  z-index: 1;
}

.problema-count {
  position: relative;
  z-index: 2;
  font-weight: 600;
  color: #333;
  font-size: 1rem;
}

.problema-percentage {
  position: absolute;
  right: 15px;
  z-index: 2;
  font-weight: 600;
  color: #333;
  font-size: 0.95rem;
}

.problema-ejemplos {
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px solid #e9ecef;
}

.problema-ejemplos details {
  cursor: pointer;
}

.problema-ejemplos summary {
  font-weight: 600;
  color: var(--color-primary);
  font-size: 0.9rem;
  margin-bottom: 10px;
}

.problema-ejemplos summary:hover {
  color: var(--color-primary-dark);
}

.ejemplos-list {
  list-style: none;
  padding: 0;
  margin: 10px 0 0 0;
}

.ejemplos-list li {
  padding: 8px 12px;
  margin-bottom: 8px;
  background: var(--color-bg);
  border-radius: 6px;
  font-size: 0.9rem;
  color: #555;
  border-left: 3px solid var(--color-primary);
}

/* Gráfico de problemas */
.problemas-chart {
  margin: 40px 0;
  padding: 30px;
  background: #f8f9fa;
  border-radius: 12px;
}

.problemas-chart h4 {
  margin: 0 0 25px 0;
  color: #333;
  text-align: center;
  font-size: 1.4rem;
  font-weight: 600;
  font-family: 'Helvetica LT Std Black Condensed Oblique', sans-serif;
}

.problemas-bars {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.problema-bar-item {
  display: flex;
  align-items: center;
  gap: 15px;
}

.problema-bar-label {
  min-width: 150px;
  font-weight: 600;
  color: #333;
  font-size: 1rem;
}

.problema-bar-wrapper {
  flex: 1;
  height: 35px;
  background: #e9ecef;
  border-radius: 18px;
  position: relative;
  overflow: hidden;
}

.problema-bar {
  height: 100%;
  background: linear-gradient(90deg, var(--color-primary) 0%, var(--color-primary-dark) 100%);
  border-radius: 18px;
  transition: width 0.8s ease;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 15px;
  min-width: 50px;
}

.problema-bar-value {
  color: white;
  font-weight: 600;
  font-size: 0.9rem;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

/* Responsive para problemas más comunes */
@media (max-width: 768px) {
  .problema-item {
    padding: 15px;
  }

  .problema-titulo {
    flex-direction: column;
    align-items: flex-start;
  }

  .problema-bar-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .problema-bar-label {
    min-width: auto;
  }

  .problema-bar-wrapper {
    width: 100%;
  }
}
</style> 